import React, { useState, useEffect } from "react";
import PatientDashboard from "../../PatientDashboard/PatientDashboard";
import PatientConsultation from "../../PatientConsultation/PatientConsultation";
import useTitanApp from "../../../hooks/useTitanApp";
import PatientList from "../../PatientConsultation/components/PatientList/PatientList";
import PatientName from "../../PatientName/PatientName";
import { Tabs, Tab } from "@mui/material";
import PatientConsultationMediaButton from "../../PatientConsultation/components/PatientConsultationMediaButton";
import PatientConsultationPlayPauseButton from "../../PatientConsultation/components/PatientConsultationPlayPauseButton";
import { setShowPlayPauseBtn } from "../../../dispatchers/patientDispatchers";
import { PrescriptionApproval } from "../../PrescriptionApproval/PrescriptionApproval";
import { PatientDashboardV2 } from "../../PatientDashboardV2/PatientDashboardV2";
import { TreatmentPlanModal } from "../../PatientConsultation/components/TreatmentPlan/TreatmentPlanModal";
import { User, FileText, Heart } from "lucide-react";
import { usePersistedState } from "@/hooks/usePersistedState";
import { useScheduleData } from "@/components/Scheduler/scheduleData";

export default function GlobalTabbedPatientContent({
  stopTranscription,
  isDemDXGlobalCheckRef,
  handlePatientInfoTabsChange,
  pauseTranscription,
  resumeTranscription,
  initializeTranscriptionAfterRefresh,
  mediaRecorderRef,
  elapsedTime,
}) {
  const overviewIcon = (
    <svg
      width="15"
      height="15"
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.6401 6.96504C13.3921 6.96504 14.0017 7.57464 14.0017 8.32661V13.3838C14.0017 14.1358 13.3921 14.7454 12.6401 14.7454H9.13898C8.38701 14.7454 7.77742 14.1358 7.77742 13.3838V8.32661C7.77742 7.57464 8.38701 6.96504 9.13898 6.96504H12.6401ZM4.87394 10.0869C5.62591 10.0869 6.2355 10.6964 6.2355 11.4484V13.3838C6.2355 14.1358 5.62591 14.7454 4.87394 14.7454H1.36156C0.609593 14.7454 0 14.1358 0 13.3838V11.4484C0 10.6964 0.609593 10.0869 1.36156 10.0869H4.87394ZM12.6401 8.1321H9.13898C9.03156 8.1321 8.94447 8.21918 8.94447 8.32661V13.3838C8.94447 13.4913 9.03156 13.5784 9.13898 13.5784H12.6401C12.7476 13.5784 12.8347 13.4913 12.8347 13.3838V8.32661C12.8347 8.21918 12.7476 8.1321 12.6401 8.1321ZM4.87394 11.2539H1.36156C1.25414 11.2539 1.16705 11.341 1.16705 11.4484V13.3838C1.16705 13.4913 1.25414 13.5784 1.36156 13.5784H4.87394C4.98136 13.5784 5.06845 13.4913 5.06845 13.3838V11.4484C5.06845 11.341 4.98136 11.2539 4.87394 11.2539ZM4.86273 0.739441C5.6147 0.739441 6.22429 1.34903 6.22429 2.101V7.15824C6.22429 7.87261 5.67413 8.45849 4.9744 8.51529L4.86273 8.51981H1.36156C0.609593 8.51981 0 7.91021 0 7.15824V2.101C0 1.34903 0.609593 0.739441 1.36156 0.739441H4.86273ZM4.86273 1.9065H1.36156C1.25414 1.9065 1.16705 1.99358 1.16705 2.101V7.15824C1.16705 7.26567 1.25414 7.35275 1.36156 7.35275H4.86273L4.90733 7.34761C4.99326 7.32746 5.05724 7.25032 5.05724 7.15824V2.101C5.05724 1.99358 4.97015 1.9065 4.86273 1.9065ZM12.6401 0.739441C13.3921 0.739441 14.0017 1.34903 14.0017 2.101V4.0461C14.0017 4.79807 13.3921 5.40766 12.6401 5.40766H9.13898C8.38701 5.40766 7.77742 4.79807 7.77742 4.0461V2.101C7.77742 1.34903 8.38701 0.739441 9.13898 0.739441H12.6401ZM9.13898 1.9065C9.03156 1.9065 8.94447 1.99358 8.94447 2.101V4.0461C8.94447 4.15352 9.03156 4.24061 9.13898 4.24061H12.6401C12.7476 4.24061 12.8347 4.15352 12.8347 4.0461V2.101C12.8347 1.99358 12.7476 1.9065 12.6401 1.9065H9.13898Z"
        className="icons-color"
      />
    </svg>
  );

  const { appState, patientState } = useTitanApp();
  const {
    patientInfoTab,
    isPatientSearched,
    isAppLoading,
    isTabLoading,
    darkMode,
    features,
  } = appState || {};
  const { servicePharmacists, serviceTypesInfo, serviceRooms } = useScheduleData();

  const { NewPatientDashboard } = features || {};
  const [isConsultationStopped, setIsConsultationStopped] = useState(false);
  const { showPlayPauseBtn, selectedPatientDetails } = patientState || {};
  // Treatment plan modal state
  const [showTreatmentPlanModal, setShowTreatmentPlanModal] = usePersistedState(
    "showTreatmentPlanModal",
    false,
  );
  const [treatmentPlanToEdit, setTreatmentPlanToEdit] = useState(null);
  const [treatmentPlanDraft, setTreatmentPlanDraft] = useState(null);
  // Handle editing treatment plan
  const handleEditTreatmentPlan = (treatmentPlan) => {
    setTreatmentPlanToEdit(treatmentPlan);
    setShowTreatmentPlanModal(true);
  };
  return (
    <div className="main-content global-tabbed-patient-content-wrapper">
      {isPatientSearched && (patientInfoTab === 0 || patientInfoTab === 1) && (
        <div className="header-content">
          <div className="left">
            <div className="patient-consultation-tabs">
              <div className="patient-name">
                <PatientName />
              </div>
              <div>
                <Tabs
                  value={patientInfoTab}
                  onChange={handlePatientInfoTabsChange}
                >
                  <Tab
                    disabled={isAppLoading || isTabLoading}
                    icon={overviewIcon}
                    iconPosition="start"
                    className="flex"
                    label={<span className="tabs_text">Dashboard</span>}
                  />
                  {/* <Tab
                    disabled={isAppLoading || isTabLoading}
                    icon={<FileText size={15} />}
                    iconPosition="start"
                    className="flex"
                    label={<span className="tabs_text">Consultations</span>}
                  /> */}
                </Tabs>
              </div>
            </div>
          </div>
          <div className="right">
            {patientInfoTab === 1 && showPlayPauseBtn && (
              <PatientConsultationPlayPauseButton
                pauseTranscription={pauseTranscription}
                resumeTranscription={resumeTranscription}
                initializeTranscriptionAfterRefresh={
                  initializeTranscriptionAfterRefresh
                }
                mediaRecorderRef={mediaRecorderRef}
              />
            )}
            {patientInfoTab === 1 && isPatientSearched && (
              <PatientConsultationMediaButton
                stopTranscription={stopTranscription}
                isConsultationStopped={isConsultationStopped}
                setIsConsultationStopped={setIsConsultationStopped}
                elapsedTime={elapsedTime}
              />
            )}
          </div>
        </div>
      )}

      {patientInfoTab === 0 &&
        (NewPatientDashboard ? (
          <PatientDashboardV2
            patient={selectedPatientDetails}
            isDarkMode={darkMode}
            onEditTreatmentPlan={handleEditTreatmentPlan}
          />
        ) : (
          <PatientDashboard />
        ))}

      <TreatmentPlanModal
        open={showTreatmentPlanModal}
        onOpenChange={(open) => {
          setShowTreatmentPlanModal(open);
          if (!open) {
            setTreatmentPlanToEdit(null);
          }
        }}
        editMode={true}
        existingPlan={treatmentPlanToEdit}
        selectedPatient={selectedPatientDetails}
        consultationId={selectedPatientDetails?.consultationId || ""}
        schedulePharmacists={servicePharmacists}      
        scheduleRoomNames={serviceRooms?.map((r) => r.name)} 
        services={serviceTypesInfo}
        onSave={(data) => {
          setShowTreatmentPlanModal(false);
          setTreatmentPlanToEdit(null);
        }}
        fromConsultationActions={false}
      />

      {patientInfoTab === 1 && (
        <PatientConsultation
          stopTranscription={stopTranscription}
          isDemDXGlobalCheckRef={isDemDXGlobalCheckRef}
        />
      )}
      {patientInfoTab === 2 && <PatientList />}
      {patientInfoTab === 3 && <PrescriptionApproval />}
    </div>
  );
}
